import * as React from 'react';
import { connect } from 'react-redux';
import actions, { ActionRequest } from '../actions';
import { ContainerContext, mapProps } from './';
import {
    WarningDialog,
    CreateCompensationBundleDialog,
    Table,
    UpdateCompensationBundleDialog,
    BaseCostumerSupportCompensationContainer,
    BaseElement,
    HeaderButton,
} from '../components';
import {
    AddOutlined,
    DeleteOutlineOutlined,
    LoyaltyOutlined as LoyaltyIcon,
    ModeEditOutlineOutlined,
} from '@mui/icons-material';
import { formatDate } from '../utils';
import { UserTitleRole } from '../enums';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import { QueryResult } from 'material-table';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
    readonly preDefinedItems: Entities.CompensationPreDefineItem[];
}

type DialogType = 'Create' | 'Edit' |'ConfirmDelete' | 'ConfirmMultiDelete';
interface State {
    readonly openDialog: DialogType | null;
    readonly selected: Entities.CompensationData[];
}

class Container extends BaseElement<Props, State> {
    state: State = {
        openDialog: null,
        selected: [],
    };

    async componentDidMount() {
        await actions.costumerSupport.listCompensationPreDefineItems();
    }

    protected renderContainer(): React.JSX.Element {
        const buttons: HeaderButton[] | undefined = this.props.userTitleRole === UserTitleRole.Viewer ? undefined : [
            { text: 'Create', icon: AddOutlined, onClick: this.openNewDialog},
        ];
        return (
            <BaseCostumerSupportCompensationContainer
                {...this.props}
                styled={this.props.styled}
                buttons = {buttons}
                showSubTabs = {true}
            >
                {this.renderContent()}
            </BaseCostumerSupportCompensationContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderNewDialog()}
                {this.renderDescriptionDialog()}
                {this.renderConfirmDeleteDialog()}
                {this.renderConfirmMultiDeleteDialog()}
            </>
        );
    }

    private renderDescriptionDialog() {
        const { preDefinedItems } = this.props;
        const { openDialog, selected } = this.state;
        if (selected.length !== 1) {
            return;
        }
        return (
            <UpdateCompensationBundleDialog
                styled={this.props.styled}
                open={openDialog === 'Edit'}
                title={'Update Compensation Bundle'}
                TitleIcon={LoyaltyIcon}
                selectedCompensationData={selected[0]}
                preDefinedItems={preDefinedItems}
                onClose={this.closeDialog}
                onUpdate={this.onUpdateCompensationData}
            />
        );
    }

    private onUpdateCompensationData = async (title: string, description: string, from: number, to:number, allUsers:boolean, userList: string, itemList: string) => {
        const { selected } = this.state;
        if (selected.length !== 1) {
            return;
        }
        
        await actions.compensationData.update(selected[0].compensationId, title, description, from, to, allUsers, userList, itemList);
        this.closeDialog();

    };
    
    private renderNewDialog = () => {
        const { preDefinedItems } = this.props;
        const { openDialog } = this.state;
        return <CreateCompensationBundleDialog
                styled={this.props.styled}
                open={openDialog === 'Create'}
                title={'Create Compensation Bundle'}
                TitleIcon={LoyaltyIcon}
                preDefinedItems={preDefinedItems}
                onClose={this.closeDialog}
                onCreate={this.onCreateCompensationData}
            />;
    };
    
    private closeDialog = () => {
        this.setState({
            openDialog: null,
            selected: [],
        });
    };

    private onCreateCompensationData = async (title: string, description: string, from: number, to:number, allUsers:boolean, userList: string, itemList: string) => {
        await actions.compensationData.create(title, description, from, to, allUsers, userList, itemList);
        this.closeDialog();
    };

    private openNewDialog = () => {
        this.setState({ openDialog: 'Create' });
    };

    private editTitleNew = () => this.setState({openDialog: 'Edit'});
    
    private openDeleteDialog = () => this.setState({openDialog: 'ConfirmDelete'});
    private renderConfirmDeleteDialog = () => {
        const { openDialog } = this.state;
        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'ConfirmDelete'}
                title={'Confirm Delete'}
                TitleIcon={LoyaltyIcon}
                content="This will permanently delete the conpensation."
                onClose={this.closeDialog}
                onSubmit={this.deleteCompensationData}
                maxWidth={'xs'}
            />
        );
    };
    private deleteCompensationData = async () => {
        const { selected } = this.state;

        this.closeDialog();
        if (selected.length === 0) {
            return;
        }

        await actions.compensationData.remove(selected[0].compensationId);
    };

    private openMultiDeleteDialog = () => this.setState({openDialog: 'ConfirmMultiDelete'});
    private renderConfirmMultiDeleteDialog = () => {
        const { openDialog } = this.state;
        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'ConfirmMultiDelete'}
                title={'Confirm Delete'}
                TitleIcon={LoyaltyIcon}
                content="This will permanently delete the conpensations."
                onClose={this.closeDialog}
                onSubmit={this.deleteMultiCompensationData}
                maxWidth={'xs'}
            />
        );
    };
    private deleteMultiCompensationData = async () => {
        const { selected } = this.state;

        this.closeDialog();
        if (selected.length === 0) {
            return;
        }


        await actions.compensationData.removeMultiple(selected.map(s => s.compensationId));
    };

    private renderContent = () => {
        const { selected } = this.state;
        const columns = [
            { title: 'Title', field: 'title'},
            { title: 'from', field: 'from'},
            { title: 'to', field: 'to'},
            { title: 'All Users', field: 'allUsers'},
        ];

        return (
            <Table
                styled={this.props.styled}
                columns={columns}
                data={this.getData}
                options={{
                    showTitle: false,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    emptyRowsWhenPaging: false,
                    search: true,
                    sorting: false,
                    draggable: false,
                    selection: true,
                }}
                actions={[
                    {
                        hidden: selected.length <= 1,
                        tooltip: 'Delete Multiple',
                        position: 'toolbar',
                        icon: DeleteOutlineOutlined,
                        onClick: this.openMultiDeleteDialog,
                    },
                    {
                        hidden: selected.length !== 1,
                        icon: ModeEditOutlineOutlined,
                        position: 'toolbar',
                        onClick: this.editTitleNew,
                    },
                    {
                        hidden: selected.length !== 1,
                        icon: DeleteOutlineOutlined,
                        position: 'toolbar',
                        onClick: this.openDeleteDialog
                    },
                ]}
                onSelectionChange={this.onSelectionChange}
            />
        );
    };

    private getData = async (query: any): Promise<QueryResult<any>> => {
        const { search, page, pageSize, orderBy, orderDirection } = query;
        const params = {
            search,
            page: page + 1,
            perPage: pageSize,
            orderBy: orderBy ? orderBy.field : null,
            orderDirection: orderDirection !== "" ? orderDirection : null,
        };
        const result = await ActionRequest.get(`compensation/list`, params);
        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
        const data = result.entities.map((e: Entities.CompensationData) => {
            return {
                title: e.title,
                from: formatDate(e.from),
                to: formatDate(e.to),
                allUsers: e.allUsers,
                compensationData: e
            };
        });

        return {
            data,
            page: result.page - 1,
            totalCount: result.totalCount,
        };
    }

    private onSelectionChange = (rows: any[]) => this.setState({ selected: rows.map(r => r.compensationData) });
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
    preDefinedItems: state.costumerSupport.preDefinedItems,
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
